import React, { memo, useEffect, useState, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import URLSearchParams from "url-search-params";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { ConfigProvider, message } from "antd";
import { IntlProvider } from "react-intl";
import Service from "../../service";
import { LoadingOutlined } from "@ant-design/icons";
import AppLocale from "lngProvider";
import MainApp from "./MainApp";
import SignIn from "../SignIn";
import ForgotPassword from "../ForgotPassword";
import ResetPassword from "../ResetPassword";
import UploadDocsCandidate from "../../components/Onboarding/UploadDocsCandidate";
import { setInitUrl, userSignInSuccess } from "appRedux/actions/Auth";
import {
  onLayoutTypeChange,
  onNavStyleChange,
  setThemeType,
} from "appRedux/actions/Setting";
import CircularProgress from "../../components/CircularProgress";

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK,
} from "../../constants/ThemeSetting";
import FeedbackForm from "../../components/Feedback/FeedbackForm";
import DayOneFeedbackForm from "../../components/Feedback/DayOneFeedbackForm";
import PreAppraisalForm from "../../components/PreAppraisal/PreAppraisalForm";
import SignedDocCandidate from "../../components/Onboarding/SignedDocCandidate";
import { userSignOut } from "../../appRedux/actions";
import DayFiveFeedbackForm from "../../components/Feedback/DayFiveFeedbackForm";
import PublicHolidays from "../../components/Holidays/PublicHolidays";

const TOOL_PERMISSION_MAP = [
  { keywords: ["taskhub"], permissionKey: "isTaskHub" },
  { keywords: ["e-connect", "econnect", "connect"], permissionKey: "isEConnect" },
  { keywords: ["crm"], permissionKey: "isECRM" },
  { keywords: ["proposal"], permissionKey: "isProposal" },
  { keywords: ["vms", "visitor"], permissionKey: "isVMS" },
];

const getRequiredPermissionForUri = (uri) => {
  if (!uri) return null;
  const lowerUri = uri.toLowerCase();
  // The VMS host may not contain the word "vms"/"visitor" (e.g. localhost:PORT),
  // so match the configured VMS URL by host to gate the "Sign in with HRMS" flow.
  try {
    const vmsUrl = process.env.REACT_APP_VMS_SIGNIN;
    if (vmsUrl) {
      const vmsHost = new URL(vmsUrl).host.toLowerCase();
      if (vmsHost && lowerUri.includes(vmsHost)) return "isVMS";
    }
  } catch (e) { /* ignore malformed REACT_APP_VMS_SIGNIN */ }
  const match = TOOL_PERMISSION_MAP.find(({ keywords }) =>
    keywords.some((kw) => lowerUri.includes(kw))
  );
  return match?.permissionKey || null;
};

const RestrictedRoute = ({
  component: Component,
  location,
  authUser,
  ...rest
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isUserTokenVerified, setIsUserTokenVerified] = useState(false);
  const accessToken = localStorage.getItem("accessToken")
    ? localStorage.getItem("accessToken")
    : null;

  useEffect(() => {
    favicon(authUser);
  }, []);
  useEffect(() => {
    getUserInfo();
  }, [history.location.pathname]);
  const getUserInfo = async () => {
    try {
      if (!authUser || !authUser._id) {
        history.push("/signin");
        return;
      }
      const params = `/${authUser._id}`;
      let response;
      if (authUser.emp_code) {
        response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.empById + params,
        });
      } else {
        response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.userById + params,
        });
      }
      if (response?.data?.code === 401) {
        // Service.sessionEnded() (triggered by the response interceptor) already
        // shows the "Session ended" modal with the real reason and redirects —
        // don't also show a generic error. But it doesn't touch this component's
        // own state, and `authUser` (Redux) can outlive a cleared localStorage
        // (e.g. permissionChange() wiping the token mid-flight) — without
        // unsticking isUserTokenVerified/authUser here too, the `!isUserTokenVerified
        // && authUser` guard below renders null forever with no way out.
        localStorage.clear();
        dispatch(userSignOut());
        history.push("/signin");
        return;
      }
      if (!response) {
        return Service.messageError(Service.error_message);
      }
      if (response.data && response.data.data) {
        const userData = response.data.data;
        const reportingmanager = response.data.is_reporting_manager;
        const assistantreportingmanager = response.data.is_assistant_reporting_manager;
        localStorage.setItem("user_data", JSON.stringify(userData));
        localStorage.setItem(
          "is_reporting_manager",
          JSON.stringify(reportingmanager)
        );
        localStorage.setItem(
          "is_assistant_reporting_manager",
          JSON.stringify(assistantreportingmanager)
        );
        localStorage.setItem(
          "resourceAccess",
          JSON.stringify(response.data?.permissions)
        );
        dispatch(userSignInSuccess(response.data.data));
        setIsUserTokenVerified(true);
      } else {
        localStorage.clear()
        history.push("/signin");
        dispatch(userSignOut())
        Service.messageError(response.data.message);
      }
    } catch (error) {
      console.log(error);
      // A real 401 lands here too — axios's response interceptor rejects the
      // promise rather than resolving with a body, so this catch (not the
      // `response?.data?.code === 401` branch above) is the actual path hit
      // when the token is missing/invalid. Same dead-end risk: without this,
      // isUserTokenVerified/authUser never unstick and the guard below renders
      // null forever. Only act if there's genuinely no token — don't sign out
      // over a transient network error while a valid session exists.
      if (!localStorage.getItem("accessToken")) {
        localStorage.clear();
        dispatch(userSignOut());
        history.push("/signin");
      }
    }
  };

  const favicon = async (userData) => {
    const id = userData?._id;
    if (id) {
      try {
        const params = `/${id}`;
        const response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.getAdminSettings + params,
        });
        const org_favicon =
          response?.data?.data?.config?.adminSetting?.fav_icon;
        localStorage.setItem("FavIconURL", org_favicon);
        changeFavIcon();
      } catch (e) {
        console.log(e);
      }
    }
  };

  const changeFavIcon = () => {
    const favicon = document.getElementById("fav_icon");
    const FavIcon = localStorage.getItem("FavIconURL"); //retrive from redux store
    //favicon.setAttribute("href", "logo192.png");
    favicon.href = FavIcon;
  };

  if (!isUserTokenVerified && authUser) {
    return null;
  }
  return (
    <Route
      {...rest}
      render={(props) =>
        authUser && accessToken ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: "/signin",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

const AuthRoute = ({ component: Component, location, authUser, ...rest }) => {
  const queryParams = new URLSearchParams(location.search);

  // Accessing a specific query parameter, e.g., 'redirect_uri'
  const redirect_uri = queryParams.get("redirect_uri");
  const encodedUrl = encodeURIComponent(redirect_uri);
  // alert(redirect_uri)
  if(redirect_uri){
    return (
      <Route
        {...rest}
        render={(props) =>
          !authUser ? (
            <Component {...props} />
          ) : (
            <Redirect
              to={{
                pathname: "/dashboard",
                search: `?redirect_uri=${encodedUrl}`,
              }}
            />
          )
        }
      />
    );
  }
  else{
    return (
      <Route
        {...rest}
        render={(props) =>
          !authUser ? (
            <Component {...props} />
          ) : (
            <Redirect
              to={{
                pathname: "/dashboard",
              }}
            />
          )
        }
      />
    );
  }
  
};

const App = (props) => {
  const dispatch = useDispatch();
  const { locale, themeType, navStyle, layoutType, themeColor } = useSelector(
    ({ settings }) => settings
  );
  const { loader, alertMessage, showMessage, authUser, initURL } = useSelector(
    ({ auth }) => auth
  );
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  useEffect(() => {
    let link = document.createElement("link");
    link.type = "text/css";
    link.rel = "stylesheet";
    link.href = `/css/${themeColor}.css`; //This line is changed, this comment is for explaination purpose.

    link.className = "gx-style";
    document.body.appendChild(link);
  }, []);

  useEffect(() => {
    if (initURL === "") {
      dispatch(setInitUrl(location.pathname));
    }
    const params = new URLSearchParams(location.search);

    if (params.has("theme")) {
      dispatch(setThemeType(params.get("theme")));
    }
    if (params.has("nav-style")) {
      dispatch(onNavStyleChange(params.get("nav-style")));
    }
    if (params.has("layout-type")) {
      dispatch(onLayoutTypeChange(params.get("layout-type")));
    }
    setLayoutType(layoutType);
    setNavStyle(navStyle);
  });

  const setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove("boxed-layout");
      document.body.classList.remove("framed-layout");
      document.body.classList.add("full-layout");
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove("full-layout");
      document.body.classList.remove("framed-layout");
      document.body.classList.add("boxed-layout");
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove("boxed-layout");
      document.body.classList.remove("full-layout");
      document.body.classList.add("framed-layout");
    }
  };

  const setNavStyle = (navStyle) => {
    if (
      navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      navStyle === NAV_STYLE_DARK_HORIZONTAL ||
      navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      navStyle === NAV_STYLE_ABOVE_HEADER ||
      navStyle === NAV_STYLE_BELOW_HEADER
    ) {
      document.body.classList.add("full-scroll");
      document.body.classList.add("horizontal-layout");
    } else {
      document.body.classList.remove("full-scroll");
      document.body.classList.remove("horizontal-layout");
    }
  };

  useEffect(() => {
    if (location.pathname === "/") {
      if (authUser === null) {
        // Not logged in — send to signin, preserving redirect_uri so SSORedirect fires after login
        if (redirect_uri) {
          history.push(`/signin?redirect_uri=${encodeURIComponent(redirect_uri)}`);
        } else {
          history.push("/signin");
        }
      } else {
        // Already logged in — if there's a redirect_uri, go straight back with ssoToken
        if (redirect_uri) {
          const requiredPermission = getRequiredPermissionForUri(redirect_uri);
          const userData = JSON.parse(localStorage.getItem("user_data"));
          if (requiredPermission && !userData?.[requiredPermission]) {
            // Keep redirect_uri so the dashboard renders the persistent
            // "You do not have permission to access this tool." denial (a bare /dashboard would lose it).
            message.error("You do not have permission to access this tool.");
            history.push(`/dashboard?redirect_uri=${encodeURIComponent(redirect_uri)}`);
          } else {
            const ssoToken = localStorage.getItem("ssoToken");
            window.location.href = `${redirect_uri}?token=${ssoToken}`;
          }
        } else if (initURL === "" || initURL === "/" || initURL === "/signin") {
          history.push("/dashboard");
        } else {
          history.push(initURL);
        }
      }
    }
  }, [authUser, initURL, location, history]);

  useEffect(() => {
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add("dark-theme");
      document.body.classList.add("dark-theme");
      let link = document.createElement("link");
      link.type = "text/css";
      link.rel = "stylesheet";
      link.href = "/css/dark_theme.css";
      link.className = "style_dark_theme";
      document.body.appendChild(link);
    }
  }, []);

  useEffect(() => {
    setSystemImages();
  }, []);

  const setSystemImages = async () => {
    try {
      const FavIcon = localStorage.getItem("FavIconURL");
      if (FavIcon) {
        const favicon = document.getElementById("fav_icon");
        favicon.href = FavIcon;
      } else {
        // const favicon = document.getElementById("fav_icon");
        // const file = ElsnerElevate;
        // const base64 = await convertBase64(file);
        // localStorage.setItem('FavIconURL', base64);
        // favicon.href = FavIcon;
      }
    } catch (e) {
      console.log(e);
    }
  };

  const currentAppLocale = AppLocale[locale.locale];
  const queryParams = new URLSearchParams(location.search);

  // Accessing a specific query parameter, e.g., 'redirect_uri'
  const redirect_uri = queryParams.get("redirect_uri");

    return (
      <ConfigProvider locale={currentAppLocale.antd}>
        <IntlProvider
          locale={currentAppLocale.locale}
          messages={currentAppLocale.messages}
        >
          <Suspense
            fallback={
              <div className="gx-loader-view">
                <CircularProgress />
              </div>
            }
          >
            {loader && (
              <div className="loader mainloader">
                <LoadingOutlined
                  style={{ fontSize: "50px", color: "#038fde" }}
                />
              </div>
            )}
            {showMessage ? message.error(alertMessage.toString()) : null}
            <Switch>
              {/* Open to literally everyone. A plain Route, not an AuthRoute — an
                  AuthRoute would bounce signed-in employees to /dashboard, and this
                  page has to render the same for them as for anonymous visitors. */}
              <Route
                exact
                path={`${match.url}holiday-list`}
                component={PublicHolidays}
              />
              <AuthRoute
                path={`${match.url}signin`}
                authUser={authUser}
                location={location}
                component={SignIn}
              />
              <AuthRoute
                path={`${match.url}forgot-password`}
                component={ForgotPassword}
              />
              <AuthRoute
                path={`${match.url}signDoc`}
                component={SignedDocCandidate}
              />
              <AuthRoute
                path={`${match.url}reset-password/:token`}
                component={ResetPassword}
              />
              <AuthRoute
                exact
                path="/upload-docs"
                component={UploadDocsCandidate}
              />
              <AuthRoute
                path={`${match.url}feedback/:id`}
                component={FeedbackForm}
              />
              <AuthRoute
                path={`${match.url}pre-appraisal/:id`}
                component={PreAppraisalForm}
              />
               <AuthRoute
                path={`${match.url}day-one-feedback`}
                component={DayOneFeedbackForm}
              />
              <AuthRoute
                path={`${match.url}day-five-feedback`}
                component={DayFiveFeedbackForm}
              />
              <RestrictedRoute
                path={`${match.url}`}
                authUser={authUser}
                location={location}
                component={MainApp}
              />
            </Switch>
          </Suspense>
        </IntlProvider>
      </ConfigProvider>
    );
  
};

export default memo(App);
